import { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import Button from '../ui/Button';
import { FINAL_CTA, APP_STORE_URL } from '../../utils/constants';
import { fadeUp, spring, viewportOnce } from '../../utils/animations';

export default function CTASection() {
  const [ref, inView] = useInView(viewportOnce);
  const sectionRef = useRef<HTMLElement>(null);

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start'],
  });

  // Glow scales up as you scroll in
  const glowScale = useTransform(scrollYProgress, [0.2, 0.6], [0.7, 1.3]);

  return (
    <section ref={sectionRef} className="section-padding relative overflow-hidden">
      {/* Scroll-linked glows */}
      <motion.div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[600px] bg-gradient-radial-purple opacity-20 gpu"
        style={{ scale: glowScale }}
      />
      <div className="absolute bottom-0 left-1/4 w-[500px] h-[500px] bg-gradient-radial-blue opacity-10 gpu" />

      <div className="container-custom relative z-10" ref={ref}>
        <div className="max-w-3xl mx-auto text-center space-y-10">
          {/* Headline */}
          <motion.h2
            className="text-5xl sm:text-6xl lg:text-7xl font-bold leading-[0.95]"
            variants={fadeUp}
            initial="hidden"
            animate={inView ? 'visible' : 'hidden'}
            transition={spring}
          >
            <span className="bg-gradient-brand bg-clip-text text-transparent whitespace-pre-line">
              {FINAL_CTA.headline}
            </span>
          </motion.h2>

          {/* Subheadline */}
          <motion.p
            className="text-xl text-white/45 max-w-lg mx-auto"
            variants={fadeUp}
            initial="hidden"
            animate={inView ? 'visible' : 'hidden'}
            transition={{ ...spring, delay: 0.12 }}
          >
            {FINAL_CTA.subheadline}
          </motion.p>

          {/* CTA */}
          <motion.div
            className="flex flex-col items-center gap-6"
            variants={fadeUp}
            initial="hidden"
            animate={inView ? 'visible' : 'hidden'}
            transition={{ ...spring, delay: 0.24 }}
          >
            <Button variant="cta" size="lg" href={APP_STORE_URL}>
              Download Free
            </Button>

            <a
              href={APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block opacity-50 hover:opacity-90 transition-opacity duration-300"
            >
              <img
                src="https://tools.applemediaservices.com/api/badges/download-on-the-app-store/white/en-us?size=250x83"
                alt="Download on the App Store"
                className="h-12"
                loading="lazy"
              />
            </a>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
